import { useMemo, useRef } from "react";
import {
  CONTENT_HUB_SOURCES,
  useContentHubEvents,
  flattenContentHubPages,
  type ContentHubItem,
  type TruthSocialPayload,
} from "@/features/content-hub";
import type { TrumpTruthStory } from "@/shared/api/firehose/models";

/** Map a normalized content-hub item into the legacy TrumpTruthStory model
 *  the firehose row components render. Payload `content` is the post text;
 *  `id` is the numeric identity (truth_id exceeds Number.MAX_SAFE_INTEGER). */
export function mapContentHubItemToTruthStory(
  item: ContentHubItem
): TrumpTruthStory {
  const payload = (item.raw ?? {}) as Partial<TruthSocialPayload>;
  const createdAt = payload.created_at ?? item.occursAt;
  return {
    id: payload.id ?? Number(item.sourceId),
    title: item.title,
    url: payload.url,
    source: "Truth Social",
    backend_source: "truth-social",
    backend_source_id: 0,
    created: createdAt,
    updated: createdAt,
    created_utc: payload.created_utc ?? createdAt,
    source_published_time: createdAt,
    truth_social_published_time: createdAt,
    tickers: item.tickers,
    isNew: item.receivedAt !== undefined && !item.wasUpdated,
    received_at: item.receivedAt,
  };
}

/**
 * Content-hub-backed replacement for the legacy useTruthSocialFeed base feed:
 * history from /v1/search (cursor-paginated), live posts from the shared
 * /v1/stream connection, adapted to the TrumpTruthStory model. Return shape
 * mirrors the legacy hook.
 *
 * Notification sounds for truth-social posts still run in the legacy global
 * ingestion pipeline (ingestFirehoseStory), which is untouched.
 */
export const useContentHubTruthSocialFeed = () => {
  const query = useContentHubEvents(CONTENT_HUB_SOURCES.TRUTH_SOCIAL);

  // Cache mapped stories per content-hub item so row object identity is
  // stable across stream patches (the row component mutates show_deatils on
  // the object; unstable identity would reset it and re-render all rows).
  const mappedCacheRef = useRef(new WeakMap<ContentHubItem, TrumpTruthStory>());

  const truthSocialStories = useMemo(() => {
    return flattenContentHubPages(query.data)
      .map((item) => {
        let story = mappedCacheRef.current.get(item);
        if (!story) {
          story = mapContentHubItemToTruthStory(item);
          mappedCacheRef.current.set(item, story);
        }
        return story;
      })
      // Defensive sort, newest first — stream patches prepend by arrival
      // order, which can differ from published order.
      .sort(
        (a, b) => Date.parse(b.created_utc || "") - Date.parse(a.created_utc || "")
      );
  }, [query.data]);

  return {
    truthSocialStories,
    isLoading: query.isPending,
    isError: query.isError,
    error: query.error,
    loadNext: query.fetchNextPage,
    isLoadingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage,
  };
};
